import React, { useState, useEffect } from 'react';
import BarraFiltros, { SinResultados } from './BarraFiltros';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    CircularProgress,
    Typography,
    Box,
    AppBar,
    Toolbar,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { styled } from '@mui/system';
import DatePicker from 'react-datepicker';
import Swal from 'sweetalert2';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faEye } from '@fortawesome/free-solid-svg-icons';
import { es } from "date-fns/locale";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
    fontWeight: 'bold',
}));

const StyledTableRow = styled(TableRow)(({ isdisabled }) => ({
    opacity: isdisabled === 'true' ? 0.5 : 1, // Atenúa los registros deshabilitados
}));

function PersonalTable() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentRow, setCurrentRow] = useState(null);
    const [newRow, setNewRow] = useState({});

    const columns = [
        { field: 'nombre', header: 'Nombre' },
        { field: 'departamento', header: 'Departamento' },
        { field: 'puesto', header: 'Puesto' },
        { field: 'extension', header: 'Extensión' },
        { field: 'numero_nomina', header: 'Número de Nómina' },
        { field: 'fecha_inicio', header: 'Fecha de Inicio' },
        { field: 'fecha_termino', header: 'Fecha de Término' },
    ];

    useEffect(() => {
        fetch('https://api.hmsaa.com.mx:5001/api/personales')
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Error HTTP: ${response.status}`);
                }
                return response.json();
            })
            .then((fetchedData) => {
                const adjustedData = fetchedData.map((item) => {
                    const inicio = item.fecha_inicio ? new Date(item.fecha_inicio) : null;
                    const termino = item.fecha_termino ? new Date(item.fecha_termino) : null;

                    return {
                        ...item,
                        // Solo guardamos el Date si es válido, si no, lo dejamos nulo
                        fecha_inicio: (inicio && !isNaN(inicio.getTime())) ? inicio : null,
                        fecha_termino: (termino && !isNaN(termino.getTime())) ? termino : null,
                    };
                });
                setData(adjustedData);
                setLoading(false);
            })
            .catch((error) => {
                setError(error.message);
                setLoading(false);
            });
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'En curso';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Fecha inválida';
            return date.toLocaleDateString('es-MX', { timeZone: 'UTC' });
        } catch (error) {
            return 'Fecha inválida';
        }
    };

    const handleAddNew = () => {
        setEditMode(false);
        setNewRow({ nombre: '', departamento: '', puesto: '', extension: '', numero_nomina: '', fecha_inicio: null, fecha_termino: null });
        setDialogOpen(true);
    };

    const handleEditRow = (row) => {
        if (row.is_disabled) return; // Bloquear edición de registros deshabilitados

        // Ajustar la fecha para la zona horaria local validando errores de "Invalid Date"
        const adjustToLocalDate = (date) => {
            if (!date) return null;
            
            const adjustedDate = new Date(date);
            
            // Validar que JS no haya creado un "Invalid Date"
            if (isNaN(adjustedDate.getTime())) {
                return null; 
            }

            // Si la fecha es válida, aplicamos el offset
            adjustedDate.setMinutes(adjustedDate.getMinutes() + adjustedDate.getTimezoneOffset());
            return adjustedDate;
        };

        setEditMode(true);
        setCurrentRow(row);
        setNewRow({
            ...row,
            fecha_inicio: adjustToLocalDate(row.fecha_inicio),
            fecha_termino: adjustToLocalDate(row.fecha_termino),
        });
        setDialogOpen(true);
    };


    const handleSaveRow = () => {
        const formatDateForSave = (date) => {
            if (date instanceof Date && !isNaN(date.getTime())) {
                const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
                return localDate.toISOString().split('T')[0];
            }
            return null;
        };
    
        const formattedRow = {
            nombre: newRow.nombre || currentRow?.nombre,
            departamento: newRow.departamento || currentRow?.departamento,
            puesto: newRow.puesto || currentRow?.puesto,
            extension: newRow.extension || currentRow?.extension,
            numero_nomina: newRow.numero_nomina || currentRow?.numero_nomina,
            fecha_inicio: formatDateForSave(newRow.fecha_inicio) || currentRow?.fecha_inicio,
            fecha_termino: formatDateForSave(newRow.fecha_termino) || currentRow?.fecha_termino,
            is_disabled: currentRow?.is_disabled || false,
        };
    
        const url = editMode
            ? `https://api.hmsaa.com.mx:5001/api/personales/${currentRow.id}`
            : 'https://api.hmsaa.com.mx:5001/api/personales';
    
        const method = editMode ? 'PUT' : 'POST';
    
        fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formattedRow),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Error HTTP: ${response.status}`);
                }
                return response.json();
            })
            .then((savedRow) => {
                if (editMode) {
                    // Actualiza el registro existente en el estado local
                    setData((prevData) => prevData.map((item) => (item.id === savedRow.id ? savedRow : item)));
                } else {
                    // Agrega el nuevo registro al estado local
                    setData((prevData) => [...prevData, savedRow]);
                }
                setDialogOpen(false);
                setNewRow({});
                Swal.fire('Éxito', editMode ? 'Registro actualizado correctamente.' : 'Registro agregado correctamente.', 'success');
            })
            .catch((error) => {
                Swal.fire('Error', `Error al guardar el registro: ${error.message}`, 'error');
            });
    };

    const handleToggleDisable = (row) => {
        const updatedRow = { is_disabled: !row.is_disabled };
    
        fetch(`https://api.hmsaa.com.mx:5001/api/personales/${row.id}/toggle-disable`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatedRow),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Error HTTP: ${response.status}`);
                }
                return response.json();
            })
            .then((savedRow) => {
                // Actualiza el registro en el estado local
                setData((prevData) =>
                    prevData.map((item) => (item.id === savedRow.id ? savedRow : item))
                );
                Swal.fire(
                    'Éxito',
                    row.is_disabled
                        ? 'Registro habilitado correctamente.'
                        : 'Registro deshabilitado correctamente.',
                    'success'
                );
            })
            .catch((error) => {
                Swal.fire('Error', `Error al actualizar el registro: ${error.message}`, 'error');
            });
    };

    const filteredData = data.filter((row) =>
        columns.some((column) =>
            row[column.field]?.toString().toLowerCase().includes(search.trim().toLowerCase())
        )
    );

    const limpiarFiltros = () => setSearch('');

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <CircularProgress />
                <Typography variant="body1">Cargando datos...</Typography>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: 'center', marginTop: '20px', color: 'red' }}>
                <Typography variant="body1">Error: {error}</Typography>
            </div>
        );
    }

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Gestión de Personal
                    </Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleAddNew}
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                    >
                        Agregar Nuevo
                    </Button>
                </Toolbar>
            </AppBar>

            <BarraFiltros
                busqueda={search}
                onBusqueda={setSearch}
                placeholder="Buscar en cualquier columna"
                total={data.length}
                mostrados={filteredData.length}
                onLimpiar={limpiarFiltros}
            />

            <TableContainer component={Paper} sx={{ margin: '20px auto', maxWidth: 1000 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <StyledTableCell key={column.field}>{column.header}</StyledTableCell>
                            ))}
                            <StyledTableCell>Acciones</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredData.map((row) => (
                            <StyledTableRow
                                key={row.id}
                                isdisabled={row.is_disabled?.toString()}
                            >
                                {columns.map((column) => (
                                    <TableCell key={column.field}>
                                        {column.field.includes('fecha')
                                            ? formatDate(row[column.field])
                                            : row[column.field]}
                                    </TableCell>
                                ))}
                                <TableCell>
                                    <Box display="flex" gap={1}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={() => handleEditRow(row)}
                                            disabled={row.is_disabled}
                                            startIcon={<FontAwesomeIcon icon={faEdit} />}
                                        >
                                            Editar
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color={row.is_disabled ? 'success' : 'error'}
                                            size="small"
                                            onClick={() => handleToggleDisable(row)}
                                        >
                                            {row.is_disabled ? 'Habilitar' : 'Deshabilitar'}
                                        </Button>
                                    </Box>
                                </TableCell>
                            </StyledTableRow>
                        ))}
                        {filteredData.length === 0 && (
                            <SinResultados colSpan={columns.length + 1} total={data.length} onLimpiar={limpiarFiltros} entidad="registros de personal" />
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                aria-labelledby="form-dialog-title"
                fullWidth
                maxWidth="sm" // Ajusta el tamaño máximo del modal
            >
                <DialogTitle id="form-dialog-title" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {editMode ? 'Editar Registro' : 'Agregar Nuevo Registro'}
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    {/* Campos no relacionados con fechas */}
                    {columns.map((column) => (
                        column.field.includes('fecha') ? null : (
                            <div key={column.field} style={{ marginBottom: '16px' }}>
                                <TextField
                                    margin="dense"
                                    label={column.header}
                                    fullWidth
                                    value={newRow[column.field] || ''}
                                    onChange={(e) =>
                                        setNewRow({ ...newRow, [column.field]: e.target.value })
                                    }
                                />
                            </div>
                        )
                    ))}

                    {/* Contenedor Responsivo de los Calendarios */}
                    <div
                        className="flex flex-wrap gap-4"
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap', // Permite apilado en pantallas pequeñas
                            gap: '16px',
                            justifyContent: 'space-between',
                        }}
                    >
                        {columns
                            .filter((column) => column.field.includes('fecha'))
                            .map((column) => (
                                <div
                                    key={column.field}
                                    className="p-4 bg-gray-100 rounded-lg shadow-md flex flex-col items-center"
                                    style={{
                                        flex: '1 1 calc(50% - 16px)', // Dos calendarios por fila
                                        minWidth: '250px', // Ancho mínimo
                                        maxWidth: 'calc(50% - 16px)', // Evita desbordamientos en pantallas grandes
                                        position: 'relative', // Asegura que el calendario se mantenga dentro del contenedor
                                        overflow: 'hidden', // Limita el contenido del calendario
                                        alignItems: 'center', // Centra horizontalmente
                                    }}
                                >
                                    <Typography
                                        style={{
                                            marginBottom: '8px',
                                            fontWeight: 'bold',
                                            color: '#374151',
                                            textAlign: 'center', // Asegura que el texto del título esté centrado
                                        }}
                                    >
                                        {column.header}
                                    </Typography>
                                    <DatePicker
                                        selected={newRow[column.field] || null}
                                        onChange={(date) =>
                                            setNewRow((prevState) => ({
                                                ...prevState,
                                                [column.field]: date,
                                            }))
                                        }
                                        dateFormat="dd/MM/yyyy"
                                        showYearDropdown
                                        scrollableYearDropdown
                                        inline
                                        locale={es}
                                        placeholderText="Selecciona una fecha"
                                        calendarClassName="rounded-lg shadow-lg bg-white border border-gray-200"
                                        dayClassName={(date) =>
                                            "text-gray-800 hover:bg-blue-100 focus:bg-blue-200 rounded-full"
                                        }
                                    />
                                    {/* Botón para borrar la fecha */}
                                    <Button
                                        onClick={() =>
                                            setNewRow((prevState) => ({
                                                ...prevState,
                                                [column.field]: null,
                                            }))
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        sx={{
                                            marginTop: '8px',
                                            alignSelf: 'center', // Centra el botón dentro del contenedor
                                        }}
                                    >
                                        Limpiar
                                    </Button>
                                </div>
                            ))}
                    </div>
                </DialogContent>
                <DialogActions
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '16px',
                    }}
                >
                    <Button
                        onClick={() => setDialogOpen(false)}
                        color="secondary"
                        variant="outlined"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => {
                            handleSaveRow(newRow); // Guarda el estado actual
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default PersonalTable;